/**
 * ResetPasswordPage — consumes /reset-password?token=… and rotates the
 * user's password via POST /api/auth/password-reset/confirm.
 */
import React, { useContext, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, KeyRound, ShieldCheck, AlertTriangle } from 'lucide-react';
import BrandMark from '../components/BrandMark';

const passwordStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: 'bg-muted' };
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw))   score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const labels = ['Very weak', 'Weak', 'Okay', 'Good', 'Strong', 'Very strong'];
  const colors = ['bg-rose-500','bg-rose-500','bg-amber-500','bg-amber-500','bg-emerald-500','bg-emerald-600'];
  return { score, label: labels[score] || '', color: colors[score] || 'bg-muted' };
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { API } = useContext(AuthContext);
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const strength = useMemo(() => passwordStrength(newPassword), [newPassword]);
  const valid = newPassword.length >= 6 && newPassword === confirm;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!token) { setError('This reset link is missing its token.'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (newPassword !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        new_password: newPassword,
      });
      setDone(true);
      toast.success('Password updated. You can sign in with your new password.');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Reset failed. Request a fresh link.';
      setError(typeof msg === 'string' ? msg : 'Reset failed. Request a fresh link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="reset-password-page">
      <header className="border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-foreground/80 hover:text-foreground" data-testid="rp-back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
          <BrandMark />
          <div className="w-8" />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <KeyRound className="w-6 h-6 text-primary" />
            </div>
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground">Choose a new password</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter a new password below. Once saved, you can sign in with it right away.
            </p>
          </div>

          {done ? (
            <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-center" data-testid="rp-success-state">
              <div className="mx-auto w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-700" />
              </div>
              <h2 className="mt-2 font-bold text-emerald-900">Password updated</h2>
              <p className="mt-1 text-sm text-emerald-800">
                Redirecting you to sign in…
              </p>
            </div>
          ) : (
            <>
              {!token && (
                <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="rp-missing-token">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>This reset link is missing its token. Request a fresh link from the <Link to="/forgot-password" className="underline font-bold">forgot password</Link> page.</span>
                </div>
              )}
            <form onSubmit={submit} className="space-y-3" data-testid="rp-form">
              <div className="space-y-1.5">
                <Label htmlFor="rp-pw" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">New password</Label>
                <Input
                  id="rp-pw"
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="h-11"
                  data-testid="rp-new-password-input"
                />
                {newPassword && (
                  <div className="mt-1">
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ${strength.color} transition-all`} style={{ width: `${(strength.score / 5) * 100}%` }} />
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid="rp-strength-label">Strength: {strength.label}</div>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rp-cf" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Confirm new password</Label>
                <Input
                  id="rp-cf"
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat your new password"
                  className="h-11"
                  data-testid="rp-confirm-password-input"
                />
              </div>
              {error && (
                <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="rp-error">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <Button
                type="submit"
                disabled={loading || !valid}
                size="lg"
                className="w-full h-12 rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="rp-submit-btn"
              >
                {loading ? 'Updating…' : 'Update password'}
              </Button>
            </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
